import { useCallback, useMemo, useRef, useState } from 'react'
import type { SortingRule } from 'react-table'

/**
 * Client-side sorting for tables that already hold every row.
 *
 * `TableV2` runs `manualSortBy`, so react-table only REPORTS which header was
 * clicked — it never reorders anything itself. A table that passes no
 * `onChangeSortBy` therefore toggles its arrow and moves no rows, which is the
 * shape AABT-70 describes.
 *
 * Use this ONLY where the whole collection is in memory — the org-structure
 * endpoints are fetched with `limit: 0`, so it is. Where the server pages the
 * rows, sorting here would mean "sort the page I happen to be looking at":
 * those tables must re-query instead, as Pipelines > Custom lists does.
 *
 * Pair with `useGetPagingHelpers` and sort BEFORE `getPaginatedList` — sorting
 * the slice would only reorder one page against itself.
 *
 * Rules are resolved as `record[rule.id]`, so a column whose `accessor` is a
 * function (its id being the derived header, not a field) sorts as missing.
 * Every column this is wired to today uses a string accessor.
 */

const isMissing = (value: unknown): boolean => value === null || value === undefined

const compareValues = (left: unknown, right: unknown): number => {
  if (typeof left === 'number' && typeof right === 'number') return left - right
  if (typeof left === 'boolean' && typeof right === 'boolean') return Number(left) - Number(right)
  // `numeric` so "Site 10" follows "Site 2" rather than preceding it, and
  // `base` so case and accents don't split otherwise-equal names.
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
}

/**
 * How to read the sort value for a column whose own id will not find it.
 *
 * Needed because a column built from a FUNCTION accessor and no explicit `id`
 * gets its id from react-table's fallback — the rendered header string. There
 * is no `record["Current budget"]`, so without a resolver such a column sorts
 * every row as blank: a control that moves nothing, which is the defect this
 * hook exists to remove rather than reproduce.
 *
 * It also decouples the sort value from the DISPLAY value, which matters more
 * than the id does. "Current budget" renders as `val2Amount(...)` — sorting
 * that string puts $1,000.00 before $9.99.
 */
export type SortAccessors<TRecord> = Record<string, (record: TRecord) => unknown>

export const applySort = <TRecord extends object>(
  records: TRecord[],
  rules: SortingRule<TRecord>[],
  accessors: SortAccessors<TRecord> = {},
): TRecord[] => {
  // Identity, not a copy: the third state of the header cycle is "unsorted",
  // which has to hand back the original order untouched.
  if (!rules?.length) return records

  // Copied before sorting. `records` is react-query's cached array, so an
  // in-place sort would reorder the cache itself and leave the unsorted state
  // unrecoverable. Array#sort is stable, so equal rows keep their order.
  return [...records].sort((left, right) => {
    for (const rule of rules) {
      // Cast, not a guard: react-table types a rule id as a plain string, and
      // no runtime check can prove an arbitrary column id is a key of TRecord.
      // A missing key reads as undefined and is handled as blank below.
      const key = String(rule.id) as keyof TRecord
      const read = accessors[String(rule.id)]
      const leftValue = read ? read(left) : left[key]
      const rightValue = read ? read(right) : right[key]

      // Blanks sink to the bottom in BOTH directions. Flipping them with the
      // direction would put empty rows above the data the user just asked to
      // see.
      if (isMissing(leftValue) || isMissing(rightValue)) {
        if (isMissing(leftValue) && isMissing(rightValue)) continue
        return isMissing(leftValue) ? 1 : -1
      }

      const result = compareValues(leftValue, rightValue)
      if (result !== 0) return rule.desc ? -result : result
    }

    return 0
  })
}

const isSameSort = <TRecord extends object>(left: SortingRule<TRecord>[], right: SortingRule<TRecord>[]): boolean =>
  left.length === right.length && left.every((rule, i) => rule.id === right[i].id && !!rule.desc === !!right[i].desc)

/**
 * `handlePageChange` comes from `useGetPagingHelpers`, and passing it is not
 * optional on a table that pages.
 *
 * `TableV2` runs `autoResetPage`, so re-sorting hands react-table a new `data`
 * array and it silently returns its OWN index to the first page — but the rows
 * are sliced by the caller's page state, which nothing reset. Sorting from
 * page 2 then leaves the pager reading "Showing 1 to 10" and highlighting
 * page 1 while the second ten rows are on screen, and clicking page 1 does
 * nothing because react-table is already there.
 */
export const useGetSortHelpers = <TRecord extends object>(
  handlePageChange?: (page: number) => void,
  accessors?: SortAccessors<TRecord>,
) => {
  const [sortBy, setSortBy] = useState<SortingRule<TRecord>[]>([])
  // `TableV2` reports its sort state from an effect that also fires on mount,
  // with no rules. Without this comparison every table using the hook would
  // jump to page 1 the moment it rendered.
  const sortByRef = useRef<SortingRule<TRecord>[]>([])

  const handleSortChange = useCallback(
    (rules: SortingRule<TRecord>[]) => {
      if (isSameSort(sortByRef.current, rules)) return
      sortByRef.current = rules
      setSortBy(rules)
      handlePageChange?.(1)
    },
    [handlePageChange],
  )

  // `accessors` is deliberately NOT a dependency. Callers build it inline, so
  // depending on it would hand back a new `sortList` every render and defeat
  // the `useMemo` in every consumer that lists it as a dependency. The map is
  // a static lookup table per table, not state.
  return useMemo(
    () => ({
      handleSortChange,
      sortList: (records: TRecord[]) => applySort(records, sortBy, accessors),
      sortBy,
    }),
    [sortBy, handleSortChange],
  )
}

export default useGetSortHelpers
